import { instrumentRunQueryKeys } from '@/config/queryKeys';
import { useAssayRunConfigs } from '@/hooks/useAssays';
import { apiClient } from '@/lib/api-client';
import {
  SAMPLE_COUNT_FALLBACK,
  findRunConfigByAssayId,
  validSampleCounts,
} from '@/pages/Instrument_Runs/components/RunEditForm/assaySelection';
import { fetchInstrumentRun, updateInstrumentRunConsumables } from '@/services/instrumentRuns';
import type { InstrumentRunConsumablesPatch } from '@/types';
import type { components as ApiComponents } from '@/types/api';
import { type UseMutationResult, useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { formatElapsedDuration } from './runStatus';
import type { InstrumentRunPublic } from './types';

// Freezes at the last computed value once `active` goes false.
export function useElapsedRunTime(startedAt: string | null | undefined, active: boolean): string | null {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!active || !startedAt) return;

    // Sync immediately — without this, `now` stays at whatever it was before this
    // render (mount time, or a stale freeze) until the first 1s tick fires.
    // oxlint-disable-next-line set-state-in-effect -- synchronizing with the real-world clock, not derived state
    setNow(Date.now());
    const intervalId = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(intervalId);
  }, [active, startedAt]);

  if (!startedAt) return null;

  const startedAtMs = new Date(startedAt).getTime();
  if (Number.isNaN(startedAtMs)) return null;

  return formatElapsedDuration(now - startedAtMs);
}

/**
 * Hook: useInstrumentRun
 * - Suspense-enabled query to fetch a single instrument run.
 * - Keep this thin: callers control caching and rendering behavior.
 */
export function useInstrumentRun(selectedOrgId: string, instrumentRunId: string) {
  return useSuspenseQuery<InstrumentRunPublic>({
    queryKey: instrumentRunQueryKeys.detail(selectedOrgId, instrumentRunId),
    queryFn: () => fetchInstrumentRun(selectedOrgId, instrumentRunId),
  });
}

/**
 * Tighten the update payload shape to only fields we intend to send.
 * This prevents accidentally sending readonly or unrelated fields.
 */
export type UpdatePayload = ApiComponents['schemas']['InstrumentRunDraftUpdate'];

/** The generated type still marks `name`/`species`/`num_samples`/`samples` nullable, but PUT answers
 *  an explicit `null` on any of them with a 422 — hence omitted, not nulled. `num_samples` is also
 *  dropped unless the assay lists it. `description` is always sent; a cleared field is `''`. */
export function buildUpdatePayload(
  data: InstrumentRunPublic | undefined,
  description: string,
  allowedSampleCounts?: readonly number[],
): UpdatePayload {
  if (!data) throw new Error('No instrument run data available to build update payload');

  const { name, species, num_samples, samples } = data;

  // `status` stays out: InstrumentRunDraftUpdate has no such field, so sending it is contract drift.
  const updatePayload: UpdatePayload = { description };

  if (name !== null && name !== undefined) updatePayload.name = name;
  if (species !== null && species !== undefined) updatePayload.species = species;
  if (Array.isArray(samples)) updatePayload.samples = samples;

  const sampleCounts =
    allowedSampleCounts && allowedSampleCounts.length > 0 ? allowedSampleCounts : SAMPLE_COUNT_FALLBACK;
  if (typeof num_samples === 'number' && sampleCounts.includes(num_samples)) {
    updatePayload.num_samples = num_samples as ApiComponents['schemas']['InstrumentRunSampleSize'];
  }

  return updatePayload;
}

export type UpdateVariables = { description: string };
export type MutationContext = { previousData?: InstrumentRunPublic };

/** Writes the description optimistically and rolls the cache back on error. The PUT still carries the
 *  run's other fields because the endpoint takes a whole InstrumentRunDraftUpdate, not a patch. */
export function useUpdateDescription(
  selectedOrgId: string,
  instrumentRunId: string,
  data?: InstrumentRunPublic,
): UseMutationResult<InstrumentRunPublic, Error, UpdateVariables, MutationContext> {
  const queryClient = useQueryClient();
  const queryKey = instrumentRunQueryKeys.detail(selectedOrgId, instrumentRunId);
  const { data: assayRunConfigs = [] } = useAssayRunConfigs();
  const allowedSampleCounts = validSampleCounts(findRunConfigByAssayId(assayRunConfigs, data?.assay_id));

  return useMutation<InstrumentRunPublic, Error, UpdateVariables, MutationContext>({
    // Named function for better stack traces in dev tools
    mutationFn: async (variables: UpdateVariables) => {
      if (!data) throw new Error('No instrument run data available to build update payload');

      const updatePayload = buildUpdatePayload(data, variables.description, allowedSampleCounts);

      const response = await apiClient.put<InstrumentRunPublic>(
        `/orgs/${selectedOrgId}/instrument_runs/${instrumentRunId}`,
        updatePayload,
      );

      if (!response) {
        throw new Error('Failed to update instrument run');
      }

      return response;
    },

    // Optimistic update: update cache immediately and return previous data for rollback.
    onMutate: async (variables: UpdateVariables) => {
      await queryClient.cancelQueries({ queryKey });

      const previousData = queryClient.getQueryData<InstrumentRunPublic>(queryKey);

      queryClient.setQueryData<InstrumentRunPublic>(queryKey, old => {
        if (!old) return old;
        // Only change the description field optimistically.
        return { ...old, description: variables.description };
      });

      return { previousData };
    },

    // Roll back to previous data if mutation fails.
    onError: (_err, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },

    // Replace cache with the server's authoritative response on success.
    onSuccess: updated => {
      queryClient.setQueryData(queryKey, updated);
    },
  });
}

/** Saves the 7 user-entered consumable fields via the status-independent PATCH endpoint,
 *  merging the server's authoritative `consumables` object back into the cached run. */
export function useUpdateInstrumentRunConsumables(selectedOrgId: string, instrumentRunId: string) {
  const queryClient = useQueryClient();
  const queryKey = instrumentRunQueryKeys.detail(selectedOrgId, instrumentRunId);

  return useMutation<InstrumentRunPublic['consumables'], Error, InstrumentRunConsumablesPatch>({
    mutationFn: async (payload: InstrumentRunConsumablesPatch) => {
      const response = await updateInstrumentRunConsumables(selectedOrgId, instrumentRunId, payload);
      return response.consumables;
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey });
    },
    onSuccess: consumables => {
      queryClient.setQueryData<InstrumentRunPublic>(queryKey, old => (old ? { ...old, consumables } : old));
    },
  });
}
